import { useCallback, useEffect, useRef, useState } from "react";

const FRIENDLY_ERRORS = {
  "not-allowed":
    "The browser didn't grant microphone access. If a permission prompt appeared, click Allow. If you don't see one, check the lock icon in your address bar.",
  "service-not-allowed":
    "Microphone access was blocked by your browser or OS. Open browser settings and allow microphone access for this site.",
  "audio-capture":
    "No microphone was detected. Check that one is connected and selected as your input device.",
  network:
    "The speech recognition service couldn't be reached. Check your internet connection and try again.",
  "language-not-supported":
    "This language isn't supported by your browser's speech engine.",
};

const CUE_WORDS = ["section", "note", "topic", "field"];

function friendly(code) {
  return FRIENDLY_ERRORS[code] || `Speech recognition error: ${code}`;
}

function detectInIframe() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch (e) {
      // ignore
    }
  }
  return "";
}

/**
 * Build a regex that detects spoken field cues in a transcript chunk.
 * Matches either:
 *   - a cue word followed by a label (e.g. "section assessment")
 *   - a bare label spoken at a phrase boundary (e.g. "...assessment patient...")
 */
function buildCueRegex(routing) {
  if (!routing || !routing.slots || !routing.slots.length) return null;
  const labels = routing.slots
    .map((s) => s.label.toLowerCase())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first to win priority
  if (!labels.length) return null;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const labelGroup = labels.map(escape).join("|");
  const cueGroup = CUE_WORDS.join("|");
  // Two alternatives:
  //   1) optional cue word ("section"/"note"/...) + label
  //   2) bare label preceded by a sentence break (start, period/comma/etc.)
  const pattern = new RegExp(
    `\\b(?:(?:${cueGroup})\\s+)?(${labelGroup})\\b[\\s:,.\\-]*`,
    "gi"
  );
  return pattern;
}

function findSlotByLabel(routing, label) {
  if (!routing || !label) return null;
  const norm = label.toLowerCase().trim();
  return routing.slots.find((s) => s.label.toLowerCase() === norm) || null;
}

/**
 * Split a fresh transcript chunk by cues and append each piece to its slot.
 * Returns updated slot map plus the active slot at the end.
 */
function routeChunk(chunk, routing, activeSlotKey, slots) {
  const updated = { ...slots };
  let activeKey = activeSlotKey || (routing.defaultSlotKey || routing.slots[0]?.key);
  const regex = buildCueRegex(routing);

  if (!regex) {
    updated[activeKey] = ((updated[activeKey] || "") + " " + chunk).trim();
    return { slots: updated, activeKey };
  }

  let lastIndex = 0;
  const matches = [];
  let m;
  while ((m = regex.exec(chunk)) !== null) {
    matches.push({ index: m.index, length: m[0].length, label: m[1] });
  }

  if (!matches.length) {
    updated[activeKey] = ((updated[activeKey] || "") + " " + chunk).trim();
    return { slots: updated, activeKey };
  }

  matches.forEach((match) => {
    const pre = chunk.slice(lastIndex, match.index).trim();
    if (pre) {
      updated[activeKey] = ((updated[activeKey] || "") + " " + pre).trim();
    }
    const slot = findSlotByLabel(routing, match.label);
    if (slot) {
      activeKey = slot.key;
    }
    lastIndex = match.index + match.length;
  });

  const tail = chunk.slice(lastIndex).trim();
  if (tail) {
    updated[activeKey] = ((updated[activeKey] || "") + " " + tail).trim();
  }
  return { slots: updated, activeKey };
}

// Captures both live transcript (Web Speech API) AND raw audio (MediaRecorder).
// Optionally routes spoken content into multiple slots when a routing config is
// passed to start(). Chrome & Edge only.
export default function useVoiceCapture() {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [slotTranscripts, setSlotTranscripts] = useState({}); // { slotKey: text }
  const [activeSlot, setActiveSlot] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [inIframe, setInIframe] = useState(false);

  const SRRef = useRef(null);
  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const finalRef = useRef("");
  const routingRef = useRef(null);
  const slotsRef = useRef({});
  const activeSlotRef = useRef(null);

  const cleanup = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.abort?.();
      } catch (e) {
        // ignore
      }
      recognitionRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch (e) {
        // ignore
      }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    const SR =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    SRRef.current = SR || null;
    setSupported(!!SR);
    setInIframe(detectInIframe());
    return cleanup;
  }, [cleanup]);

  const start = useCallback(
    async (initialTranscript = "", options = {}) => {
      const SR = SRRef.current;
      // Note: SR may be null (Firefox, Safari iOS). We still record audio so
      // the caller can transcribe with Whisper afterwards (record-only mode).
      // Caller can also explicitly force record-only via options.skipSpeechRecognition.
      const skipSR = !!options.skipSpeechRecognition;

      cleanup();

      setError(null);
      setErrorCode(null);
      finalRef.current = initialTranscript;
      setTranscript(initialTranscript);
      setAudioUrl(null);
      setAudioBlob(null);

      // Routing
      const routing = options.routing || null;
      routingRef.current = routing;
      const initialSlots = routing
        ? routing.slots.reduce((acc, s) => {
            acc[s.key] = "";
            return acc;
          }, {})
        : {};
      slotsRef.current = initialSlots;
      setSlotTranscripts(initialSlots);
      const initialActive = routing
        ? routing.initialSlotKey || routing.defaultSlotKey || routing.slots[0]?.key
        : null;
      activeSlotRef.current = initialActive;
      setActiveSlot(initialActive);

      // Acquire mic stream for MediaRecorder.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        setErrorCode("not-allowed");
        setError(friendly("not-allowed"));
        return;
      }
      streamRef.current = stream;

      // MediaRecorder for audio capture
      const mimeType = pickMimeType();
      let recorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (e) {
        recorder = null;
      }

      const chunks = [];
      if (recorder) {
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = () => {
          const blob = new Blob(chunks, {
            type: recorder.mimeType || "audio/webm",
          });
          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            setAudioUrl(url);
            setAudioBlob(blob);
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          // In record-only mode (no Web Speech) the recorder's onstop is the
          // canonical end-of-capture event, so flip listening off here too.
          if (!SRRef.current || skipSR) setListening(false);
        };
        try {
          recorder.start();
        } catch (e) {
          // ignore
        }
        recorderRef.current = recorder;
      }

      // Web Speech is optional — skip if the browser doesn't support it or
      // if the caller explicitly opted out.
      if (!SR || skipSR) {
        setListening(true);
        return;
      }

      // SpeechRecognition
      const recognition = new SR();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            const chunk = result[0].transcript.trim();
            if (chunk) {
              finalRef.current = (finalRef.current + " " + chunk).trim();
              if (routingRef.current) {
                const out = routeChunk(
                  chunk,
                  routingRef.current,
                  activeSlotRef.current,
                  slotsRef.current
                );
                slotsRef.current = out.slots;
                activeSlotRef.current = out.activeKey;
                setSlotTranscripts({ ...out.slots });
                setActiveSlot(out.activeKey);
              }
            }
          } else {
            interim += result[0].transcript;
          }
        }
        setTranscript((finalRef.current + " " + interim).trim());
      };

      recognition.onerror = (event) => {
        const code = event.error;
        if (code && code !== "no-speech" && code !== "aborted") {
          setErrorCode(code);
          setError(friendly(code));
          setListening(false);
        }
      };

      recognition.onend = () => {
        setListening(false);
      };

      recognitionRef.current = recognition;

      try {
        recognition.start();
        setListening(true);
      } catch (e) {
        setErrorCode("start-failed");
        setError(
          "Couldn't start the microphone. Tap the mic button once more."
        );
        setListening(false);
      }
    },
    [cleanup]
  );

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // ignore
      }
    }
    if (recorderRef.current && recorderRef.current.state === "recording") {
      try {
        recorderRef.current.stop();
      } catch (e) {
        // ignore
      }
    }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    finalRef.current = "";
    setTranscript("");
    slotsRef.current = {};
    setSlotTranscripts({});
    activeSlotRef.current = null;
    setActiveSlot(null);
    setAudioUrl(null);
    setAudioBlob(null);
    setError(null);
    setErrorCode(null);
  }, []);

  const setManual = useCallback((value) => {
    finalRef.current = value;
    setTranscript(value);
  }, []);

  const setActiveSlotManual = useCallback((key) => {
    activeSlotRef.current = key;
    setActiveSlot(key);
  }, []);

  const dismissError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  return {
    supported,
    listening,
    transcript,
    slotTranscripts,
    activeSlot,
    audioUrl,
    audioBlob,
    error,
    errorCode,
    inIframe,
    start,
    stop,
    reset,
    setManual,
    setActiveSlot: setActiveSlotManual,
    dismissError,
  };
}
